import { Injectable, Signal, signal, Type } from '@angular/core';

// ============================================
// INTERFACES & TYPES
// ============================================

/**
 * Configuration for an action that triggers modal closure.
 * Used by closeSignal to decouple modal closing from button handlers.
 */
export interface ModalCloseConfig {
  /** The action that triggered the close */
  action: string;
  /** Optional reason for the action */
  reason?: string;
  /** Optional custom data to pass with the close */
  data?: any;
}

/**
 * Configuration for opening a modal dialog.
 * Defines the component to render, its inputs, action buttons, and behavior.
 *
 * Signal-Based Closure Flow:
 * 1. Host component (e.g., FormBuilderComponent) creates: `const closeSignal = signal(null)`
 * 2. Host component passes closeSignal to ModalConfig.closeSignal
 * 3. Button handlers call: `closeSignal.set({ action: 'save', data: ... })`
 * 4. ModalContainerComponent watches closeSignal via effect
 * 5. When closeSignal is set, container calls service.closeModal(result)
 * 6. Effect completes, host component can reset signal if needed
 *
 * Important: The signal must be consumed/reset after processing to avoid redundant firings.
 * The ModalContainerComponent handles this by checking for null values.
 */
export interface ModalConfig<T = any> {
  /** Component to render inside the modal */
  component: Type<any>;

  /** Component inputs (passed as properties on the component instance) */
  componentInputs?: Partial<T>;

  /** Modal title displayed in the header */
  title?: string;

  /** Modal size preset */
  size?: 'small' | 'medium' | 'large' | 'full';

  /**
   * Signal that triggers modal closure when set (decoupled from button handlers).
   *
   * Pattern:
   * const closeSignal = signal<ModalCloseConfig | null>(null);
   * closeSignal.set({ action: 'save', data: myData });
   * // ModalContainerComponent watches this and closes the modal
   *
   * The container resets the tracking of the signal value after processing to prevent repeated closures.
   */
  closeSignal?: Signal<ModalCloseConfig | null>;

  /** Action buttons rendered in the modal footer */
  actionButtons?: ModalActionButton[];
  footerClassName?: string; // Optional custom class for the footer

  /** Callback fired after modal closes */
  onClose?: () => void | Promise<void>;

  /** Callback fired when modal is cancelled */
  onCancel?: () => void | Promise<void>;

  /** Custom handler for header close button click */
  onHeaderCloseClick?: () => void | Promise<void>;

  /** Custom handler for clicking outside the modal */
  onClickOutside?: () => void | Promise<void>;

  /** Whether pressing Escape closes the modal (default: true) */
  closeOnEscape?: boolean;

  /** Whether clicking outside closes the modal (default: true) */
  closeOnClickOutside?: boolean;

  /** Whether to show the close button in the header (default: true) */
  showCloseButton?: boolean;

  /** Accessibility label for the modal */
  ariaLabel?: string;
}

/**
 * Result returned when a modal closes.
 * Always resolves (never rejects) - closing is a normal outcome, not an error.
 */
export interface ModalResult<T = any> {
  /** What action caused the close (e.g., 'save', 'delete', 'cancel', 'close') */
  action: string;

  /** Why the action happened */
  reason?: 'user_action' | 'escape_key' | 'backdrop_click' | 'header_close_button';

  /** Custom data from the component or button handler */
  data?: T;
}

/**
 * Definition of an action button in the modal footer.
 *
 * The button can control its disabled state via a Signal<string | null>:
 * - null: button is enabled
 * - string: button is disabled with this reason message (shown in tooltip)
 *
 * Optional beforeClick handler can perform async validation before onClick:
 * - Returns true: proceed to onClick handler
 * - Returns false: block action (button already handles disabled state)
 * Useful for async validation, state checks, etc.
 */
export interface ModalActionButton {
  /** Unique identifier for the button */
  id?: string;

  /** Button label text */
  label: string;

  /** Button visual variant */
  variant?: 'primary' | 'secondary' | 'danger' | 'text';

  /** Click handler with access to the modal reference */
  onClick: (event: ModalButtonClickEvent) => void | Promise<void>;

  /** Whether the button is disabled (static boolean or reactive Signal with reason) */
  disabled?: boolean | Signal<boolean> | Signal<string | null>;

  /** Optional async handler called before onClick */
  beforeClick?: Signal<() => Promise<boolean>>;

  /** Whether the button shows a loading state (reactive Signal) */
  loading?: Signal<boolean>;

  /** Custom CSS class for the button */
  className?: string;

  /**
   * Override the data-testid attribute on the rendered button.
   * If not set, falls back to `modal-button-{id}` or `modal-button-{label}`.
   * Use this to give field-type-specific testids (e.g. `text-edit-save`).
   */
  testId?: string;
}

/**
 * Event passed to button click handlers.
 */
export interface ModalButtonClickEvent {
  /** The original DOM MouseEvent */
  originalEvent: MouseEvent;

  /** The button definition that was clicked */
  button: ModalActionButton;
}

/**
 * Reference provided to components and callbacks for controlling the modal.
 *
 * Design principle: Single close() method.
 * All close paths (button, escape, backdrop) resolve the same Promise.
 * Result object indicates what action caused the close.
 * Never rejects - closing is a normal outcome, not an error.
 */
export abstract class ModalReference {
  /**
   * Close the modal with an optional result.
   *
   * @example
   * modal.close({ action: 'save', data: updatedField });
   * modal.close({ action: 'cancel' });
   * modal.close({ action: 'delete' });
   */
  abstract close<T = any>(result?: ModalResult<T>): void;

  /**
   * Access the injected component instance.
   * Useful for extracting state from the component (Pattern B).
   *
   * @example
   * const component = modal.getComponent() as FieldEditComponent;
   * const data = component.getData();
   */
  abstract getComponent(): any;

  /** Whether the modal is currently open */
  abstract isOpen(): boolean;

  /** Get the current modal configuration */
  abstract getConfig(): ModalConfig;
}

// ============================================
// Internal types for modal stack management
// ============================================

interface ModalStackEntry {
  config: ModalConfig;
  resolver: (result?: ModalResult<any>) => void;
  componentInstance: any;
}

/**
 * Service for managing dynamic modal dialogs.
 *
 * Singleton service that manages modal state, queuing, and lifecycle.
 * Works 100% offline - uses only signals, no HTTP or WebSocket dependencies.
 *
 * Usage:
 * ```typescript
 * const result = await this.modalService.openModal({
 *   component: MyComponent,
 *   title: 'Edit Item',
 *   componentInputs: { item: myItem },
 *   actionButtons: [
 *     { label: 'Save', variant: 'primary', onClick: ({ modal }) => modal.close({ action: 'save' }) },
 *     { label: 'Cancel', variant: 'text', onClick: ({ modal }) => modal.close({ action: 'cancel' }) },
 *   ],
 * });
 *
 * if (result?.action === 'save') {
 *   // Handle save
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class ModalService {
  // ============================================
  // PRIVATE STATE
  // ============================================

  private readonly _isOpen = signal(false);
  private readonly _config = signal<ModalConfig | null>(null);
  private readonly _modalStack = signal<ModalStackEntry[]>([]);
  private readonly _activeModalCount = signal(0);
  private readonly _zIndex = signal(1000);

  // ============================================
  // PUBLIC SIGNALS (read-only)
  // ============================================

  /** Whether a modal is currently open */
  readonly isOpen: Signal<boolean> = this._isOpen.asReadonly();

  /** Current modal configuration (null if closed) */
  readonly config: Signal<ModalConfig | null> = this._config.asReadonly();

  /** Number of modals currently stacked */
  readonly activeModalCount: Signal<number> = this._activeModalCount.asReadonly();

  /** Z-index for the current modal layer */
  readonly zIndex: Signal<number> = this._zIndex.asReadonly();

  // ============================================
  // PUBLIC API
  // ============================================

  /**
   * Open a modal with the given configuration.
   * Returns a Promise that resolves when the modal closes.
   * The Promise always resolves (never rejects).
   *
   * Supports signal-based closure:
   * If config.closeSignal is provided, the button handlers can call:
   *   closeSignal.set({ action: 'save', data: ... })
   * And DynamicModalDialogComponent will watch this signal and emit closeModal,
   * which triggers close via ModalContainerComponent.onDialogClose().
   *
   * This decouples button logic from modal lifecycle - the button just sets the signal,
   * the component handles the closing via its effect.
   *
   * @param config Modal configuration including component, inputs, buttons, and behavior
   * @returns Promise resolving to ModalResult when the modal closes, or undefined
   *
   * @example
   * const closeSignal = signal<ModalCloseConfig | null>(null);
   * const result = await this.modalService.openModal({
   *   component: MyComponent,
   *   closeSignal,
   *   actionButtons: [
   *     {
   *       label: 'Save',
   *       onClick: () => closeSignal.set({ action: 'save', data: ... })
   *     }
   *   ]
   * });
   */
  openModal<T = any>(config: ModalConfig<T>): Promise<ModalResult<T> | undefined> {
    return new Promise<ModalResult<T> | undefined>((resolve) => {
      const entry: ModalStackEntry = {
        config,
        resolver: resolve as (result?: ModalResult<any>) => void,
        componentInstance: null,
      };

      // Add to stack
      this._modalStack.update((stack) => [...stack, entry]);

      // Update current modal signals
      this._config.set(config);
      this._isOpen.set(true);
      this._activeModalCount.update((count) => count + 1);
      this._zIndex.set(1000 + this._modalStack().length * 10);
    });
  }

  /**
   * Close the current (topmost) modal with an optional result.
   * @param result The result to resolve the modal's Promise with
   */
  closeModal<T = any>(result?: ModalResult<T>): void {
    const stack = this._modalStack();
    if (stack.length === 0) {
      return;
    }

    // Pop the topmost modal
    const entry = stack[stack.length - 1];
    this._modalStack.update((s) => s.slice(0, -1));
    this._activeModalCount.update((count) => Math.max(0, count - 1));

    // Update signals based on remaining stack
    const remaining = this._modalStack();
    if (remaining.length === 0) {
      this._isOpen.set(false);
      this._config.set(null);
      this._zIndex.set(1000);
    } else {
      // Show previous modal in stack
      const previous = remaining[remaining.length - 1];
      this._config.set(previous.config);
      this._zIndex.set(1000 + remaining.length * 10);
    }

    // Resolve the Promise
    entry.resolver(result);
  }

  /**
   * Set the component instance for the current modal.
   * Called by ModalContainerComponent after creating the dynamic component.
   */
  setComponentInstance(instance: any): void {
    const stack = this._modalStack();
    if (stack.length > 0) {
      stack[stack.length - 1].componentInstance = instance;
    }
  }

  /**
   * Get the component instance of the current modal.
   * Used by ModalReference.getComponent().
   */
  getComponentInstance(): any {
    const stack = this._modalStack();
    if (stack.length > 0) {
      return stack[stack.length - 1].componentInstance;
    }
    return null;
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /** Whether a modal is currently open */
  isModalOpen(): boolean {
    return this._isOpen();
  }

  /** Get the current modal configuration */
  getCurrentConfig(): ModalConfig | null {
    return this._config();
  }

  /** Get the current z-index for modal layering */
  getZIndex(): number {
    return this._zIndex();
  }
}
